import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  PaginatedResult,
  ProductType,
  StocktakeDetail,
  StocktakeLineSummary,
  StocktakeStatus,
  StocktakeSummary,
} from '@optiflow/types';
import { api } from '@/services/apiClient';
import { inventoryKeys } from './inventory.api';

export interface StocktakeListParams {
  page?: number;
  pageSize?: number;
  status?: StocktakeStatus | '';
  search?: string;
}

export interface StocktakeLineParams {
  page?: number;
  pageSize?: number;
  search?: string;
  variancesOnly?: boolean;
  uncountedOnly?: boolean;
}

export const stocktakeKeys = {
  all: ['stocktakes'] as const,
  list: (params: StocktakeListParams) => [...stocktakeKeys.all, 'list', params] as const,
  detail: (id: string) => [...stocktakeKeys.all, 'detail', id] as const,
  lines: (id: string, params: StocktakeLineParams) =>
    [...stocktakeKeys.all, 'lines', id, params] as const,
};

export function useStocktakesQuery(params: StocktakeListParams) {
  return useQuery({
    queryKey: stocktakeKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResult<StocktakeSummary>>('/stocktakes', {
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 20,
          ...(params.status ? { status: params.status } : {}),
          ...(params.search?.trim() ? { search: params.search.trim() } : {}),
        },
      }),
    placeholderData: (previous) => previous,
  });
}

export function useStocktakeQuery(id: string | undefined) {
  return useQuery({
    queryKey: stocktakeKeys.detail(id ?? ''),
    queryFn: () => api.get<StocktakeDetail>(`/stocktakes/${id ?? ''}`),
    enabled: Boolean(id),
  });
}

export function useStocktakeLinesQuery(id: string | undefined, params: StocktakeLineParams) {
  return useQuery({
    queryKey: stocktakeKeys.lines(id ?? '', params),
    queryFn: () =>
      api.get<PaginatedResult<StocktakeLineSummary>>(`/stocktakes/${id ?? ''}/lines`, {
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 25,
          ...(params.search?.trim() ? { search: params.search.trim() } : {}),
          ...(params.variancesOnly ? { variancesOnly: true } : {}),
          ...(params.uncountedOnly ? { uncountedOnly: true } : {}),
        },
      }),
    enabled: Boolean(id),
    placeholderData: (previous) => previous,
  });
}

/** Counting touches the sheet; posting touches stock. Both invalidate inventory to be safe. */
function useStocktakeInvalidation() {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({ queryKey: stocktakeKeys.all });
    void queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
  };
}

export function useOpenStocktakeMutation() {
  const invalidate = useStocktakeInvalidation();

  return useMutation({
    mutationFn: (payload: {
      name?: string;
      scopeCategoryId?: string;
      scopeProductType?: ProductType;
      includeZeroStock?: boolean;
      notes?: string;
    }) => api.post<StocktakeDetail>('/stocktakes', payload),
    onSuccess: invalidate,
  });
}

export function useRecordCountsMutation(id: string) {
  const invalidate = useStocktakeInvalidation();

  return useMutation({
    mutationFn: (lines: { lineId: string; countedQuantity: number; notes?: string }[]) =>
      api.post<StocktakeDetail>(`/stocktakes/${id}/counts`, { lines }),
    onSuccess: invalidate,
  });
}

export function usePostStocktakeMutation(id: string) {
  const invalidate = useStocktakeInvalidation();

  return useMutation({
    mutationFn: (payload: { treatUncountedAsZero?: boolean; notes?: string }) =>
      api.post<StocktakeDetail>(`/stocktakes/${id}/post`, payload),
    onSuccess: invalidate,
  });
}

export function useCancelStocktakeMutation(id: string) {
  const invalidate = useStocktakeInvalidation();

  return useMutation({
    mutationFn: (reason: string) =>
      api.post<StocktakeDetail>(`/stocktakes/${id}/cancel`, { reason }),
    onSuccess: invalidate,
  });
}
